import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, Hero, In, List, Related, Steps, WhatIs, WorkflowCard } from "@/components/seo/kit";

// /for-freelancers: segment page. Rewritten 2026-09-29 for today's product
// (SEO run: gtm/products/clearmakes-inbox/seo/). Examples are illustrations, not customers.

const PATH = "/for-freelancers";

const faqs = [
  {
    q: "Is the free plan enough for a freelancer?",
    a: "Often, to start. Free connects 3 apps and runs 2 new workflows and 100 runs a month, plus 1 saved answer. If you rely on it every day, Starter is $29 a month for 5,000 runs and 25 saved answers.",
  },
  {
    q: "Will clients know a workflow replied?",
    a: "Only if the reply reads like a template. Write saved answers in your own words, start with drafts you check, and end each one with how to reach you directly.",
  },
  {
    q: "Can I keep each client's emails separate?",
    a: "Yes. Describe one workflow per client or per project, for example “when Acme emails, add a note to the Acme page in Notion”.",
  },
];

const ForFreelancers = () => (
  <>
    <PageMeta
      title="Email Automation for Freelancers | ClearMakes Inbox"
      description="For freelancers juggling client email: answer rate and availability questions from saved answers, file client notes in Notion and log invoices."
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Solutions", path: "/solutions" },
        { name: "Freelancers", path: PATH },
      ]}
    />
    <main>
      <Hero
        eyebrow="Solutions · Freelancers"
        title="Email automation for freelancers with too many clients to answer"
        lead="The same questions about your rates and availability, notes to file for every client, invoices to keep track of. Let workflows do the typing and filing."
        secondary={{ label: "All solutions", href: "/solutions" }}
      />

      <WhatIs title="What does ClearMakes Inbox do for a freelancer?">
        For a freelancer, ClearMakes Inbox answers the questions clients ask again and again from your saved answers, files notes from client
        emails into Notion, and keeps track of invoices and payment emails. You describe each workflow in one sentence and check the steps. The
        free plan is enough to start; Starter is $29 a month.
      </WhatIs>

      <Band tone="pale" title="Three workflows freelancers start with" answer="Start with what eats your mornings: repeat questions, client notes and payment emails.">
        <div className="grid gap-5 lg:grid-cols-3">
          <WorkflowCard
            sentence="When someone asks about my rates or availability, draft a reply with my day rate and booking link."
            steps={["When an email arrives in Gmail", "Check it asks about rates or availability", "Draft a reply", "Wait for you to check it"]}
          />
          <WorkflowCard
            sentence="When Acme emails, add a note with the request and any deadline to the Acme page in Notion."
            steps={["When an email arrives in Gmail", "Check it is from Acme", "Pull out the details: request, deadline", "Add to the Acme page in Notion"]}
          />
          <WorkflowCard
            sentence="When a client says an invoice is paid, add the label Paid and note the amount."
            steps={["When an email arrives in Gmail", "Check it confirms a payment", "Pull out the details: amount, invoice number", "Add the label Paid"]}
          />
        </div>
      </Band>

      <Band tone="white" title="Set it up in an afternoon" answer="Save the answers you type most, then add one workflow at a time. Keep drafts on until you trust the wording.">
        <Steps
          items={[
            { title: "Connect Gmail or Outlook", body: <>From the Apps page in <In href="/products/inbox">ClearMakes Inbox</In>. Add Notion if you keep client notes there.</> },
            { title: "Save your most common answer", body: <>Rates, availability or how you work, in <In href="/smart-faq">Smart FAQ</In>. The free plan keeps one.</> },
            { title: "Describe one client workflow", body: "One sentence per client or per kind of email." },
            { title: "Check the first runs", body: "Each run shows what it filed, labelled or drafted." },
          ]}
        />
      </Band>

      <Band tone="pale" title="Who this is not for" answer="If you get a handful of emails a day, you don't need this: Gmail or Outlook templates are enough. Inbox pays off when the same emails keep coming." narrow>
        <List
          items={[
            "Not an invoicing tool: it can note and label payment emails, not send invoices.",
            "Not a client portal or CRM.",
            <>Need templates first? Use the free <In href="/guides/auto-reply-to-common-customer-questions">guide to auto-replying to common questions</In>.</>,
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/smart-faq", label: "Smart FAQ", desc: "Saved answers to the questions clients ask." },
          { href: "/guides/auto-reply-to-common-customer-questions", label: "Auto-reply templates", desc: "8 templates for common questions." },
          { href: "/gmail-integration", label: "Gmail automation", desc: "What a workflow can do with new email." },
          { href: "/for-founders", label: "For founders", desc: "Running the company inbox." },
          { href: "/solutions", label: "All solutions", desc: "ClearMakes Inbox by team." },
          { href: "/pricing", label: "Pricing", desc: "Free, Starter, Growth and Enterprise." },
        ]}
      />

      <Closing title="Stop typing the same answers to clients." line="Connect your mailbox and save your first answer." />
    </main>
    <Footer />
  </>
);

export default ForFreelancers;
